// File: /js/shared/quiz/multiple-choice-toggle.js
// Role: Shared UI logic for toggling single vs multiple correct answers
// Works with AnswerInputs layout in UPLOAD and EDIT modes.
// Structural goals:
// - idempotent initialization
// - resilient to partial DOM replacement on the left side (is_multiple checkbox)
// - no reliance on replacing the whole answers block
// - explicit contest_quiz mode helper for poll/graded switching

import { i18n } from '../utils/config.js';

const SECTION_SELECTOR =
  'section.answer-blocks[data-block-type="quiz-answers"][data-mode="edit"], ' +
  'section.answer-blocks[data-block-type="quiz-answers"][data-mode="upload"]';

const SECTION_BOUND_ATTR = 'data-multiple-choice-bound';
const GLOBAL_BOUND_FLAG  = '__MULTIPLE_CHOICE_GLOBAL_BOUND__';
const MODEL_GLOBAL_FLAG  = '__QUESTION_MODEL_GLOBAL_BOUND__';

// "Modèle de question" veneer select (Kprime §3). The visible control;
// drives the hidden is_multiple checkbox + answer_format field below.
const MODEL_SELECT_SELECTOR = 'select[data-question-model="true"]';

export function initMultipleChoice(root = document) {
  bindGlobalIsMultipleListener();
  bindGlobalModelSelectListener();

  findAnswerSections(root).forEach((section) => {
    bindSectionOnce(section);
    syncSectionFromCurrentMode(section);
  });

  // On initial render the model select carries the server-composed value;
  // apply its UI (kprime layout in particular) WITHOUT dispatching the
  // persist events — nothing has changed yet.
  const modelSelect = findModelSelect(root);
  if (modelSelect) {
    applyQuestionModelUI(modelSelect.value, root);
  }
}

/**
 * Apply contest_quiz UI mode locally without replacing the whole answers block.
 *
 * Rules:
 * - poll:
 *   - hide/disable all "Réponse juste" toggles
 *   - uncheck all toggles
 *   - hide is_multiple and force it off
 *
 * - graded:
 *   - show/enable all "Réponse juste" toggles
 *   - force is_multiple off
 *   - force exactly first answer checked by default
 */
export function applyQuestionScoringModeUI(mode, root = document) {
  const section = findFirstAnswerSection(root);
  if (!section) return;

  const allowMultiple = findAllowMultipleCheckbox(root);

  if (mode === 'poll') {
    if (allowMultiple) {
      allowMultiple.checked = false;
      setIsMultipleVisibility(allowMultiple, false);
    }

    section.dataset.showCorrectToggle = '0';

    const toggles = ensureAllCorrectToggleWrappers(section);
    toggles.forEach((cb) => {
      cb.checked = false;
      cb.disabled = true;
    });

    setCorrectToggleVisibility(section, false);
    refreshAnswerBlockClasses(section);
    return;
  }

  if (mode === 'graded') {
    if (allowMultiple) {
      allowMultiple.checked = false;
      setIsMultipleVisibility(allowMultiple, true);
    }

    section.dataset.showCorrectToggle = '1';

    const toggles = ensureAllCorrectToggleWrappers(section);
    toggles.forEach((cb, index) => {
      cb.disabled = false;
      cb.checked = (index === 0);
    });

    setCorrectToggleVisibility(section, true);
    refreshAnswerBlockClasses(section);
    setMultipleMode(section, false);
  }
}

/* -------------------------------------------------------------------------- */
/* internals                                                                  */
/* -------------------------------------------------------------------------- */

function findAnswerSections(root = document) {
  return Array.from(root.querySelectorAll(SECTION_SELECTOR));
}

function findFirstAnswerSection(root = document) {
  return root.querySelector(SECTION_SELECTOR);
}

function findAllowMultipleCheckbox(root = document) {
  return root.querySelector('input[type="checkbox"][name="is_multiple"]');
}

function bindGlobalIsMultipleListener() {
  if (typeof window === 'undefined') return;
  if (window[GLOBAL_BOUND_FLAG]) return;

  window[GLOBAL_BOUND_FLAG] = true;

  document.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'checkbox') return;
    if (target.name !== 'is_multiple') return;

    const section = findFirstAnswerSection(document);
    if (!section) return;

    setMultipleMode(section, target.checked);
    refreshAnswerBlockClasses(section);
  });
}

/* -------------------------------------------------------------------------- */
/* Modèle de question (Kprime §3) — the veneer select over two columns        */
/* -------------------------------------------------------------------------- */

function findModelSelect(root = document) {
  return root.querySelector(MODEL_SELECT_SELECTOR);
}

function findAnswerFormatInput(root = document) {
  return root.querySelector('input[name="answer_format"]');
}

function isKprimeMode(section) {
  return section instanceof HTMLElement && section.classList.contains('kprime-mode');
}

function dispatchInputChange(el) {
  if (!(el instanceof HTMLElement)) return;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

function bindGlobalModelSelectListener() {
  if (typeof window === 'undefined') return;
  if (window[MODEL_GLOBAL_FLAG]) return;
  window[MODEL_GLOBAL_FLAG] = true;

  document.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (target.getAttribute('data-question-model') !== 'true') return;

    applyQuestionModel(target.value, document);
  });
}

/**
 * User changed the model select. Decompose into the two stored columns
 * (single → standard/0, multiple → standard/1, kprime → kprime/0), drive the
 * hidden inputs (which persist via their own editInput in edit mode), then
 * apply the answer-block UI. `dispatchInputChange` is what makes the
 * single/multiple reshape AND the inline persistence happen — the existing
 * is_multiple listener picks up the checkbox change.
 */
function applyQuestionModel(model, root = document) {
  const isKprime   = model === 'kprime';
  const wantMultiple = model === 'multiple';

  // Persist is_multiple FIRST, then answer_format. Order matters for the
  // server's answer_format downshift normalization (EditHelper): on a
  // kprime→multiple switch, is_multiple→1 must land before answer_format→
  // standard so the server doesn't see (standard, is_multiple=0) mid-flight
  // and wrongly collapse the multi-correct rows to a single correct answer.
  const allowMultiple = findAllowMultipleCheckbox(root);
  if (allowMultiple && allowMultiple.checked !== wantMultiple) {
    allowMultiple.checked = wantMultiple;
    // Drives the existing single↔multiple reshape + editInput persistence.
    allowMultiple.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const answerFormat = findAnswerFormatInput(root);
  if (answerFormat) {
    const next = isKprime ? 'kprime' : 'standard';
    if (answerFormat.value !== next) {
      answerFormat.value = next;
      dispatchInputChange(answerFormat);
    }
  }

  applyQuestionModelUI(model, root);
}

/**
 * Apply the answer-block UI for a model value WITHOUT persisting — used on
 * initial render and as the tail of `applyQuestionModel`. Kprime gets its own
 * authoring layout (every statement keeps an independent truth toggle, no
 * cardinality enforcement); single/multiple defer to the standard sync path.
 */
function applyQuestionModelUI(model, root = document) {
  const section = findFirstAnswerSection(root);
  if (!section) return;

  if (model === 'kprime') {
    applyKprimeAuthoringUI(section);
    return;
  }

  section.classList.remove('kprime-mode');
  syncSectionFromCurrentMode(section);
}

/**
 * Kprime authoring layout: every statement shows an independent "Vrai"
 * (is_correct) toggle, any number may be true (incl. zero), and NONE of the
 * single/multiple cardinality rules apply. Marked by the `kprime-mode` class
 * which gates the enforcement helpers below.
 */
function applyKprimeAuthoringUI(section) {
  if (!(section instanceof HTMLElement)) return;

  section.classList.add('kprime-mode');
  // Independent multi-select toggle styling (each statement stands alone).
  section.classList.add('multiple-mode');
  section.dataset.showCorrectToggle = '1';

  const toggles = ensureAllCorrectToggleWrappers(section);
  toggles.forEach((cb) => { cb.disabled = false; });

  setCorrectToggleVisibility(section, true);
  refreshAnswerBlockClasses(section);
}

function bindSectionOnce(section) {
  if (!(section instanceof HTMLElement)) return;
  if (section.getAttribute(SECTION_BOUND_ATTR) === '1') return;

  section.setAttribute(SECTION_BOUND_ATTR, '1');

  wireSingleChoiceBehavior(section);
  observeDynamicAnswers(section);
  guardAgainstUncheckingTheOnlyOne(section);
}

function syncSectionFromCurrentMode(section) {
  const showCorrectToggle = section.dataset.showCorrectToggle !== '0';
  const allowMultiple = findAllowMultipleCheckbox(document);

  if (!showCorrectToggle) {
    applyQuestionScoringModeUI('poll', document);
    return;
  }

  if (allowMultiple) {
    setIsMultipleVisibility(allowMultiple, true);
    setMultipleMode(section, allowMultiple.checked);
  } else {
    setMultipleMode(section, false);
  }

  refreshAnswerBlockClasses(section);
}

function findCorrectToggles(section) {
  return Array.from(
    section.querySelectorAll('.answer-correct-toggle input[type="checkbox"]')
  ).filter((cb) => !cb.disabled);
}

function ensureAtLeastOneCorrect(section) {
  const toggles = findCorrectToggles(section);
  if (!toggles.length) return;

  if (!toggles.some(cb => cb.checked)) {
    toggles[0].checked = true;
  }
}

function enforceSingleChoice(section) {
  const toggles = findCorrectToggles(section);
  if (!toggles.length) return;

  let firstChecked = toggles.find(cb => cb.checked) || null;
  if (!firstChecked) {
    toggles[0].checked = true;
    firstChecked = toggles[0];
  }

  toggles.forEach(cb => {
    if (cb !== firstChecked) cb.checked = false;
  });
}

function setMultipleMode(section, allowMulti) {
  // Kprime owns its own layout (every statement independent, no cardinality
  // rule) — keep the multi-select styling and skip single/multiple enforcement.
  if (isKprimeMode(section)) {
    section.classList.add('multiple-mode');
    return;
  }

  const isMultiple = !!allowMulti;
  section.classList.toggle('multiple-mode', isMultiple);

  if (section.dataset.showCorrectToggle === '0') {
    return;
  }

  if (isMultiple) {
    ensureAtLeastOneCorrect(section);
  } else {
    enforceSingleChoice(section);
  }
}

function wireSingleChoiceBehavior(section) {
  section.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'checkbox') return;
    if (!target.closest('.answer-correct-toggle')) return;
    if (target.disabled) return;

    // Kprime: each statement's truth is independent — no single/multiple
    // enforcement, just refresh the visual correct/incorrect classes.
    if (isKprimeMode(section)) {
      refreshAnswerBlockClasses(section);
      return;
    }

    const multiple = isMultipleMode();

    if (multiple) {
      ensureAtLeastOneCorrect(section);
      refreshAnswerBlockClasses(section);
      return;
    }

    const toggles = findCorrectToggles(section);

    if (target.checked) {
      toggles.forEach(cb => {
        if (cb !== target) cb.checked = false;
      });
      refreshAnswerBlockClasses(section);
      return;
    }

    const someOtherChecked = toggles.some(cb => cb !== target && cb.checked);
    if (!someOtherChecked) {
      target.checked = true;
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      e.stopPropagation?.();
      e.preventDefault?.();
    }

    refreshAnswerBlockClasses(section);
  });
}

function guardAgainstUncheckingTheOnlyOne(section) {
  const shouldBlockToggle = (el) => {
    if (!(el instanceof HTMLInputElement) || el.type !== 'checkbox') return false;
    if (!el.closest('.answer-correct-toggle')) return false;
    if (el.disabled) return false;
    if (isKprimeMode(section)) return false;
    if (isMultipleMode()) return false;
    if (!el.checked) return false;

    const toggles = findCorrectToggles(section);
    return toggles.filter(cb => cb.checked).length === 1;
  };

  section.addEventListener('mousedown', (e) => {
    const el = e.target;
    if (shouldBlockToggle(el)) {
      e.preventDefault();
    }
  }, true);

  section.addEventListener('keydown', (e) => {
    const el = e.target;
    if (e.code === 'Space' || e.key === ' ' || e.keyCode === 32) {
      if (shouldBlockToggle(el)) {
        e.preventDefault();
      }
    }
  }, true);
}

function observeDynamicAnswers(section) {
  if (!('MutationObserver' in window)) return;

  const container = section.querySelector('.blocks-container') || section;

  const rerun = () => {
    bindSectionOnce(section);

    // Kprime: a newly-added statement gets an enabled, independent truth
    // toggle; no single/multiple enforcement runs.
    if (isKprimeMode(section)) {
      const toggles = ensureAllCorrectToggleWrappers(section);
      toggles.forEach((cb) => { cb.disabled = false; });
      setCorrectToggleVisibility(section, true);
      refreshAnswerBlockClasses(section);
      return;
    }

    if (section.dataset.showCorrectToggle === '0') {
      const toggles = ensureAllCorrectToggleWrappers(section);
      toggles.forEach((cb) => {
        cb.checked = false;
        cb.disabled = true;
      });
      setCorrectToggleVisibility(section, false);
      refreshAnswerBlockClasses(section);
      return;
    }

    const toggles = ensureAllCorrectToggleWrappers(section);
    toggles.forEach((cb) => {
      cb.disabled = false;
    });
    setCorrectToggleVisibility(section, true);

    if (isMultipleMode()) {
      ensureAtLeastOneCorrect(section);
    } else {
      enforceSingleChoice(section);
    }

    refreshAnswerBlockClasses(section);
  };

  const obs = new MutationObserver(rerun);
  obs.observe(container, { childList: true, subtree: true });
}

function isMultipleMode() {
  const allowMultiple = findAllowMultipleCheckbox(document);
  return !!(allowMultiple && allowMultiple.checked);
}

function setIsMultipleVisibility(allowMultipleCheckbox, visible) {
  if (!(allowMultipleCheckbox instanceof HTMLInputElement)) return;

  const wrapper =
    allowMultipleCheckbox.closest('.checkbox-field') ||
    allowMultipleCheckbox.closest('.form-field') ||
    allowMultipleCheckbox.parentElement;

  if (!wrapper) return;

  wrapper.classList.toggle('invisible', !visible);
}

function setCorrectToggleVisibility(section, visible) {
  section.querySelectorAll('.answer-correct-toggle').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.classList.toggle('invisible', !visible);
  });
}

function refreshAnswerBlockClasses(section) {
  const blocks = Array.from(section.querySelectorAll('.blocks-container .block'));

  blocks.forEach((block) => {
    const checkbox = block.querySelector('.answer-correct-toggle input[type="checkbox"]');
    const isCorrect = checkbox instanceof HTMLInputElement && !checkbox.disabled && checkbox.checked;

    block.classList.toggle('block-true-answer', !!isCorrect);
    block.classList.toggle('block-false-answer', !isCorrect);

    if (!isCorrect) {
      block.classList.add('required-false');
    } else {
      block.classList.remove('required-false');
    }
  });
}

function ensureAllCorrectToggleWrappers(section) {
  const blocks = Array.from(section.querySelectorAll('.blocks-container .block'));

  return blocks.map((block, index) => ensureCorrectToggleForBlock(section, block, index));
}

function ensureCorrectToggleForBlock(section, block, index) {
  let wrapper = block.querySelector('.answer-correct-toggle');

  if (!wrapper) {
    wrapper = buildCorrectToggleWrapper(section, block, index);

    const header = block.querySelector('.block-header');
    const actions = header?.querySelector('.block-actions');

    if (header && actions) {
      header.insertBefore(wrapper, actions);
    } else if (header) {
      header.appendChild(wrapper);
    }
  }

  const input = wrapper.querySelector('input[type="checkbox"]');
  if (!(input instanceof HTMLInputElement)) {
    throw new Error('Correct toggle wrapper created without checkbox input.');
  }

  return input;
}

function buildCorrectToggleWrapper(section, block, index) {
  const wrapper = document.createElement('span');
  wrapper.className = 'answer-correct-toggle';

  const label = document.createElement('label');
  label.className = 'checkbox-inline';

  const input = document.createElement('input');
  input.type = 'checkbox';
  input.value = '1';

  const inputId = `${section.dataset.blockName || 'answer_blocks'}-answer-${index}-correct`;
  input.id = inputId;

  if (section.dataset.mode === 'edit') {
    input.name = 'is_correct';
    input.setAttribute('data-ajax-onchange', 'true');
    input.setAttribute('data-name', 'editInput');
    input.setAttribute('data-page', 'content');
    input.setAttribute('data-key', 'answer_blocks');

    const blockId = block.dataset.blockId;
    if (blockId) {
      input.setAttribute('data-id', blockId);
    }
  } else {
    const blockName = section.dataset.blockName || 'answer_blocks';
    input.name = `${blockName}[${index}][is_correct]`;
  }

  const text = document.createElement('span');
  text.className = 'toggle-label';
  text.textContent = i18n('block.answer.correct_toggle_label');

  label.setAttribute('for', inputId);
  label.appendChild(input);
  label.appendChild(text);
  wrapper.appendChild(label);

  return wrapper;
}

/* ---------- auto-init when loaded directly on a page ---------- */

if (typeof window !== 'undefined' && !window.__QUIZ_MULTIPLE_CHOICE_AUTO_INIT__) {
  window.__QUIZ_MULTIPLE_CHOICE_AUTO_INIT__ = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMultipleChoice(document));
  } else {
    initMultipleChoice(document);
  }
}